import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MixtureCategoryDTO } from '@scip/api';
import { Table } from 'primeng/table';
import { map, take } from 'rxjs/operators';

import { MixtureCategoryService } from '../../../services';
import { ChangeDetectorRef } from '@angular/core';
import { translationKeys } from '../../../core';
import { typeField } from '@avano/avano-components/utils';
import { TableColumnConfiguration } from '@avano/avano-components/avano-table';

@Component({
  selector: 'scip-mixture-selection-dialog',
  templateUrl: './mixture-selection-dialog.component.html',
  styleUrls: ['./mixture-selection-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MixtureSelectionDialogComponent implements OnInit {
  @Input()
  set selectedCategories(val: MixtureCategoryDTO[]) {
    if (val && val.length !== 0) {
      this._selectedCategories = [...val];
    }
  }
  get selectedCategories(): MixtureCategoryDTO[] {
    return this._selectedCategories;
  }

  @Output() readonly saveEvent: EventEmitter<MixtureCategoryDTO[]> = new EventEmitter<MixtureCategoryDTO[]>();
  @Output() readonly cancelEvent: EventEmitter<void> = new EventEmitter<void>();

  cols: TableColumnConfiguration[];
  selectedCategoriesColumns: TableColumnConfiguration[];
  columnKeys = {
    text: typeField<MixtureCategoryDTO>('text')
  };
  filteredMixtures: MixtureCategoryDTO[] = [];
  selectedNodeLeft: MixtureCategoryDTO;
  selectedNodeRight: MixtureCategoryDTO;
  showDialog = true;
  translationKeys: typeof translationKeys = translationKeys;

  private allMixtures: MixtureCategoryDTO[] = [];
  private _selectedCategories: MixtureCategoryDTO[] = [];

  constructor(private mixtureCategoryService: MixtureCategoryService, private changeDet: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.cols = [
      {
        field: this.columnKeys.text,
        header: translationKeys.mixtureSelectDialogComponent.html.column.categories,
        filterable: true,
        filterConfiguration: {
          filterField: this.columnKeys.text
        }
      }
    ];
    this.selectedCategoriesColumns = [
      {
        field: this.columnKeys.text,
        header: translationKeys.mixtureSelectDialogComponent.html.column.selection
      }
    ];

    // As long as we do not show the categories as a tree (hierarchical), we just hide the not selectable parent categories
    // The hierarchically tree view should come with SCIP-956
    this.mixtureCategoryService
      .getAllMixtureCategories()
      .pipe(
        map((allCategories: MixtureCategoryDTO[]) => allCategories.filter((singleCategory: MixtureCategoryDTO) => singleCategory.selectable)),
        map((allSelectableCategories: MixtureCategoryDTO[]) => this.sortCategories(allSelectableCategories)),
        take(1)
      )
      .subscribe((selectableCategories: MixtureCategoryDTO[]) => {
        this.allMixtures = selectableCategories;
        this.checkSavedSelectedCategories();
        this.changeDet.markForCheck();
      });
  }

  transferSelection(): void {
    if (this.selectedNodeLeft) {
      this._selectedCategories.push(this.selectedNodeLeft);
      this.filteredMixtures.splice(this.filteredMixtures.indexOf(this.selectedNodeLeft), 1);
      this.updateAfterTransfer();
    }
  }

  removeSelectedNode(): void {
    if (this.selectedNodeRight) {
      this._selectedCategories.splice(this._selectedCategories.indexOf(this.selectedNodeRight), 1);
      this.filteredMixtures.push(this.selectedNodeRight);

      this.updateAfterTransfer();
    }
  }

  removeAll(): void {
    this._selectedCategories = [];
    this.filteredMixtures = [...this.allMixtures];
    this.updateAfterTransfer();
  }

  setAvailableLeft(nodeAvailableCategory: MixtureCategoryDTO): void {
    this.selectedNodeLeft = nodeAvailableCategory;
    this.selectedNodeRight = undefined;
  }

  setSelectedRight(selectedCategory: MixtureCategoryDTO): void {
    this.selectedNodeLeft = undefined;
    this.selectedNodeRight = selectedCategory;
  }

  // Filter out the already added categories from the selectable table on the left side
  private checkSavedSelectedCategories(): void {
    if (this._selectedCategories) {
      this.filteredMixtures = this.allMixtures.filter((cat) => {
        return !this._selectedCategories.some((selectedCategory) => cat.identifier === selectedCategory.identifier);
      });
    } else {
      this._selectedCategories = [];
      this.filteredMixtures = [...this.allMixtures];
    }
  }

  private updateAfterTransfer(): void {
    // No matter in which direction we transfer something we discard the selection on both sides
    this.selectedNodeRight = undefined;
    this.selectedNodeLeft = undefined;
    this.filteredMixtures = this.sortCategories(this.filteredMixtures);
    this._selectedCategories = this.sortCategories(this._selectedCategories);
  }

  private sortCategories(unsortedCategories: MixtureCategoryDTO[]): MixtureCategoryDTO[] {
    // Sort mutates the array, because of primeNG we need a new one, therefore spread it ;)
    return [...unsortedCategories.sort((a, b) => (a.text > b.text ? 1 : b.text > a.text ? -1 : 0))];
  }
}
